import { useEffect, useState } from "react";
import { Roles } from "../../types/user.types";
import { useAuthGuard } from "../../hooks/useAuthGuard";
import HeaderNavBar from "../../components/header-nav-bar";
import { useHealthService } from "../../services/health.service";
import { NamedHealthReport, HealthStatus } from "../../types/health-check.types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { InlineSpinner } from "../../components/inline-spinner";

export default function HealthCheck() {
  useAuthGuard(Roles.Admin);
  const { getHealthStatus } = useHealthService();
  const [healthReports, setHealthReports] = useState<NamedHealthReport[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHealth = async () => {
      setLoading(true);
      setError(null);
      const data = await getHealthStatus();
      if (!data) {
        setError("Failed to fetch health status from backend");
      } else {
        setHealthReports(data);
      }
      setLoading(false);
    };

    fetchHealth();
  }, [getHealthStatus]);

  return (
    <div className="container">
      <HeaderNavBar backTo="dashboard" />
      <div className="row tablet-view justify-content-start">
        <div className="col-4 tablet-left d-flex flex-column">
          <div className="tablet-buffer d-flex flex-fill flex-column mb-2">
            <div className="mb-auto">
              <span>Administrator</span>
              <h2>Health Check</h2>
            </div>
          </div>
        </div>
        <div className="col-8 tablet-right">
          <div className="tablet-buffer">
            {loading ? (
              <div className="text-center p-4">
                <InlineSpinner />
                <p className="mt-2">Loading health status...</p>
              </div>
            ) : error ? (
              <div className="alert alert-danger" role="alert">
                <strong>Error:</strong> {error}
              </div>
            ) : healthReports && healthReports.length > 0 ? (
              <div>
                {healthReports.map((report, reportIndex) => {
                  const isHealthy = report.status === HealthStatus.Healthy;
                  return (
                    <div key={reportIndex}>
                      {/* Overall Health Status Header */}
                      <div className="health-status-header mb-4 p-3 bg-light border rounded">
                        <h4 className="mb-3">{report.name}</h4>
                        <div className="row align-items-center">
                          <div className="col-auto">
                            {isHealthy ? (
                              <FontAwesomeIcon icon={faCheckCircle} size="2x" className="text-success" />
                            ) : (
                              <FontAwesomeIcon icon={faTimesCircle} size="2x" className="text-danger" />
                            )}
                          </div>
                          <div className="col">
                            <div className="row">
                              <div className="col-md-4">
                                <strong>HEALTH</strong>
                              </div>
                              <div className="col-md-4">
                                <strong>LAST EXECUTION</strong>
                                <p className="mb-0">{new Date().toLocaleString()}</p>
                              </div>
                              <div className="col-md-4">
                                <strong>TOTAL DURATION</strong>
                                <p className="mb-0">{report.totalDuration}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Health Check Entries Table */}
                      <div className="table-responsive border rounded mb-4">
                        <table className="table table-sm mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: "20%" }}>NAME</th>
                              <th style={{ width: "10%" }}>HEALTH</th>
                              <th style={{ width: "50%" }}>DESCRIPTION</th>
                              <th style={{ width: "20%" }}>DURATION</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(report.entries || {}).length > 0 ? (
                              Object.entries(report.entries).map(([entryName, entry]) => (
                                <tr key={entryName}>
                                  <td>
                                    <strong>{entryName}</strong>
                                  </td>
                                  <td>
                                    <FontAwesomeIcon
                                      icon={entry.status === HealthStatus.Healthy ? faCheckCircle : faTimesCircle}
                                      className={entry.status === HealthStatus.Healthy ? "text-success" : "text-danger"}
                                    />
                                  </td>
                                  <td>
                                    <small>{entry.description || "-"}</small>
                                  </td>
                                  <td>
                                    <small>{entry.duration || "-"}</small>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={4} className="text-center text-muted">
                                  No health checks available
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="alert alert-info" role="alert">
                No health reports available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
